/**
 * Confirmation Dialog Component
 *
 * A reusable confirmation dialog for destructive actions with:
 * - Multiple variants (danger, warning, info)
 * - Focus trap for accessibility
 * - Keyboard support — Escape always cancels. Enter activates whichever
 *   button currently has focus, same as any native button; Cancel is
 *   focused on open (see the effect below) as a deliberate safety
 *   default for destructive actions, so a stray Enter press cancels
 *   rather than confirms. Tab to Confirm (or click it) to proceed via
 *   keyboard — this is intentionally NOT overridden to make Enter
 *   confirm unconditionally, which would make an accidental Enter
 *   press destructive.
 * - Loading state for async confirmations
 * - Customizable content and buttons
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { X, AlertTriangle, Trash2, AlertCircle, Info, Loader } from 'lucide-react';

// ============================================
// Types
// ============================================

export type ConfirmDialogVariant = 'danger' | 'warning' | 'info';

export interface ConfirmDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Close handler */
  onClose: () => void;
  /** Confirm handler - can be async */
  onConfirm: () => void | Promise<void>;
  /** Dialog title */
  title: string;
  /** Dialog message/description */
  message: string | React.ReactNode;
  /** Confirm button text */
  confirmText?: string;
  /** Cancel button text */
  cancelText?: string;
  /** Visual variant */
  variant?: ConfirmDialogVariant;
  /** Loading state (for async operations) */
  isLoading?: boolean;
  /** Custom icon */
  icon?: React.ReactNode;
  /** Additional content below message */
  children?: React.ReactNode;
}

// ============================================
// Hook for Confirm Dialog State
// ============================================

interface UseConfirmDialogOptions<T = void> {
  onConfirm: (data: T) => void | Promise<void>;
  onCancel?: () => void;
}

interface ConfirmDialogState<T = void> {
  isOpen: boolean;
  data: T | null;
  isLoading: boolean;
}

export function useConfirmDialog<T = void>(options: UseConfirmDialogOptions<T>) {
  const [state, setState] = React.useState<ConfirmDialogState<T>>({
    isOpen: false,
    data: null,
    isLoading: false,
  });

  const open = useCallback((data: T) => {
    setState({ isOpen: true, data, isLoading: false });
  }, []);

  const close = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
    options.onCancel?.();
  }, [options]);

  const confirm = useCallback(async () => {
    if (state.data === null) return;

    setState(prev => ({ ...prev, isLoading: true }));
    try {
      await options.onConfirm(state.data);
      setState({ isOpen: false, data: null, isLoading: false });
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [state.data, options]);

  return {
    isOpen: state.isOpen,
    data: state.data,
    isLoading: state.isLoading,
    open,
    close,
    confirm,
  };
}

// ============================================
// Main Component
// ============================================

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false,
  icon,
  children,
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const [isConfirming, setIsConfirming] = React.useState(false);

  // Focus trap and keyboard handling
  useEffect(() => {
    if (!isOpen) return;

    // Focus cancel button when dialog opens
    cancelButtonRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading && !isConfirming) {
        onClose();
      }

      // Trap focus within dialog
      if (e.key === 'Tab') {
        const focusableElements = dialogRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );

        if (!focusableElements || focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Prevent body scroll
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose, isLoading, isConfirming]);

  // Handle confirm with loading
  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      setIsConfirming(false);
    }
  };

  if (!isOpen) return null;

  const loading = isLoading || isConfirming;

  // Variant-specific styles
  const variantStyles = {
    danger: {
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      defaultIcon: <Trash2 className="w-6 h-6" />,
      confirmButton: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    },
    warning: {
      iconBg: 'bg-amber-100',
      iconColor: 'text-amber-600',
      defaultIcon: <AlertTriangle className="w-6 h-6" />,
      confirmButton: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
    },
    info: {
      iconBg: 'bg-primary/10',
      iconColor: 'text-primary',
      defaultIcon: <Info className="w-6 h-6" />,
      confirmButton: 'bg-primary hover:brightness-95 focus:ring-primary/50',
    },
  };

  const styles = variantStyles[variant];

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-dialog-title"
      aria-describedby="confirm-dialog-description"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={!loading ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Dialog */}
      <div
        ref={dialogRef}
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all animate-in zoom-in-95 duration-200"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          disabled={loading}
          className="absolute top-4 right-4 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          aria-label="Close dialog"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Content */}
        <div className="p-6">
          {/* Icon */}
          <div className={`mx-auto w-12 h-12 rounded-full ${styles.iconBg} ${styles.iconColor} flex items-center justify-center`}>
            {icon || styles.defaultIcon}
          </div>

          {/* Title */}
          <h3
            id="confirm-dialog-title"
            className="mt-4 text-lg font-semibold text-gray-900 text-center"
          >
            {title}
          </h3>

          {/* Message */}
          <div
            id="confirm-dialog-description"
            className="mt-2 text-sm text-gray-600 text-center"
          >
            {message}
          </div>

          {/* Additional content */}
          {children && (
            <div className="mt-4">
              {children}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 bg-gray-50 rounded-b-xl border-t">
          <button
            ref={cancelButtonRef}
            type="button"
            onClick={onClose}
            disabled={loading}
            className="flex-1 px-4 py-2.5 rounded-lg font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {cancelText}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={loading}
            className={`
              flex-1 inline-flex items-center justify-center gap-2
              px-4 py-2.5 rounded-lg font-medium text-white
              transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2
              disabled:opacity-50 disabled:cursor-not-allowed
              ${styles.confirmButton}
            `}
          >
            {loading ? (
              <>
                <Loader className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Convenience Components
// ============================================

interface DeleteConfirmDialogProps extends Omit<ConfirmDialogProps, 'variant' | 'title' | 'confirmText'> {
  /** Item name being deleted */
  itemName?: string;
  /** Item type for title */
  itemType?: string;
}

export const DeleteConfirmDialog: React.FC<DeleteConfirmDialogProps> = ({
  itemName,
  itemType = 'item',
  message,
  ...props
}) => (
  <ConfirmDialog
    variant="danger"
    title={`Delete ${itemType}?`}
    message={message || (itemName
      ? `Are you sure you want to delete "${itemName}"? This action cannot be undone.`
      : `Are you sure you want to delete this ${itemType}? This action cannot be undone.`
    )}
    confirmText="Delete"
    {...props}
  />
);

interface UnsavedChangesDialogProps extends Omit<ConfirmDialogProps, 'variant' | 'title' | 'message' | 'confirmText'> {
  /** Custom message */
  message?: string;
}

export const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
  message = 'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.',
  ...props
}) => (
  <ConfirmDialog
    variant="warning"
    title="Unsaved Changes"
    message={message}
    confirmText="Leave"
    cancelText="Stay"
    icon={<AlertCircle className="w-6 h-6" />}
    {...props}
  />
);

export default ConfirmDialog;
